import React from "react";
import { useParams } from "react-router-dom";
import MasterLayout from "../masterLayout/MasterLayout";
import Breadcrumb from "../components/Breadcrumb";
import { useAtom } from "jotai";
import { eventsAtom, settingsAtom } from "../utils/atoms";
import { Icon } from '@iconify/react';
import { eventTypesTexts } from "../utils/requests/events";


const transactionType = {
    cash: "במזומן",
    credit_card: "בכרטיס אשראי",
    card: "בכרטיס אשראי",
    check: "בשיק",
    bank_transfer: "בהעברה בנקאית",
    paypal: "בפייפאל",
    bit: "בביט",
    paybox: "בפייבוקס",
    other: "",
    student_balance: "מיתרת התלמיד",
}
const EventDetailsPage = () => {
    const { id } = useParams();
    const [events] = useAtom(eventsAtom);
    useAtom(settingsAtom);

    const event = events.find(e => e.id === id);

    if (!event) {
        return (
            <MasterLayout>
                <Breadcrumb title="פרטי אירוע" />
                <div className="container mt-4">
                    <div className="alert alert-warning">האירוע לא נמצא</div>
                </div>
            </MasterLayout>
        );
    }

    const renderField = (label, value, icon = null) => {
        if (!value && value !== 0) return null;
        return (
            <div className="row mb-3">
                <div className="col-md-3">
                    <label className="form-label fw-semibold text-primary-light">
                        {icon && <Icon icon={icon} className="block" style={{ marginBottom: 4, marginLeft: 4 }} />}
                        {label}:
                    </label>
                </div>
                <div className="col-md-9">
                    <p className="mb-0">{value}</p>
                </div>
            </div>
        );
    };

    const renderStudentInfo = () => {
        const student = event.extendedProps?.student;
        if (!student) return null;

        return (
            <div className="card mb-4">
                <div className="card-header">
                    <p className="mb-0 flex items-center text-xl">
                        <Icon icon="mdi:account" className="block" style={{ marginBottom: 4, marginLeft: 4 }} />
                        פרטי תלמיד
                    </p>
                </div>
                <div className="card-body">
                    {renderField("שם מלא", `${student.firstName} ${student.lastName}`, "mdi:account")}
                    {renderField("טלפון", student.phoneNumber, "mdi:phone")}
                    {renderField("אימייל", student.email, "mdi:email")}
                    {renderField("הערות תלמיד", student.note, "mdi:note-text")}
                </div>
            </div>
        );
    };

    const renderDateTimeInfo = () => {
        return (
            <div className="card mb-4">
                <div className="card-header">
                    <p className="mb-0 flex items-center text-xl">
                        <Icon icon="mdi:calendar-clock" className="block" style={{ marginBottom: 4, marginLeft: 4 }} />
                        זמן האירוע
                    </p>
                </div>
                <div className="card-body">
                    {renderField("תאריך", new Date(event.start).toLocaleDateString('he-IL'), "mdi:calendar")}
                    {renderField("שעת התחלה", new Date(event.start).toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' }), "mdi:clock-start")}
                    {renderField("שעת סיום", new Date(event.end).toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' }), "mdi:clock-end")}
                </div>
            </div>
        );
    };

    const renderLocationInfo = () => {
        const pickupLocation = event.extendedProps?.pickupLocation;
        const dropoffLocation = event.extendedProps?.dropoffLocation;

        if (!pickupLocation && !dropoffLocation) return null;

        return (
            <div className="card mb-4">
                <div className="card-header">
                    <p className="mb-0 flex items-center text-xl">
                        <Icon icon="mdi:map-marker" className="block" style={{ marginBottom: 4, marginLeft: 4 }} />
                        מיקום
                    </p>
                </div>
                <div className="card-body">
                    {renderField("מיקום איסוף", pickupLocation, "mdi:map-marker")}
                    {renderField("מיקום הורדה", dropoffLocation, "mdi:map-marker")}
                </div>
            </div>
        );
    };

    const renderFinancialInfo = () => {
        const price = event.extendedProps?.price;
        const transactionId = event.extendedProps?.transactionId;

        const types = ['lesson', 'test', 'internalTest']
        if (!types.includes(event.extendedProps?.type)) return null;
        if (!price && !transactionId) return null;

        return (
            <div className="card mb-4">
                <div className="card-header">
                    <p className="mb-0 flex items-center text-xl">
                        <Icon icon="mdi:currency-usd" className="block" style={{ marginBottom: 4, marginLeft: 4 }} />
                        תשלומים
                    </p>
                </div>
                <div className="card-body">
                    {transactionId && renderField("תשלום", transactionId ? `שולם - ${transactionType[event.extendedProps?.transaction?.type]}` : "", "mdi:receipt")}
                    {price && renderField("מחיר", `₪${price}`, "mdi:currency-usd")}
                </div>
            </div>
        );
    };

    const renderCarInfo = () => {
        const car = event.extendedProps?.car;
        if (!car) return null;

        return (
            <div className="card mb-4">
                <div className="card-header">
                    <p className="mb-0 flex items-center text-xl">
                        <Icon icon="mdi:car" className="block" style={{ marginBottom: 4, marginLeft: 4 }} />
                        פרטי רכב
                    </p>
                </div>
                <div className="card-body">
                    {renderField("מספר רכב", car.plateNumber, "mdi:car")}
                    {renderField("דגם", car.model, "mdi:car")}
                    {renderField("צבע", car.color, "mdi:palette")}
                </div>
            </div>
        );
    };

    const renderSettingsInfo = () => {
        const requestStudentApproval = event.extendedProps?.requestStudentApproval;
        const showNoteToStudent = event.extendedProps?.showNoteToStudent;
        const isRepeating = event.extendedProps?.repeatingEventId;

        if (!requestStudentApproval && !showNoteToStudent && !isRepeating) return null;

        return (
            <div className="card mb-4">
                <div className="card-header">
                    <p className="mb-0 flex items-center text-xl">
                        <Icon icon="mdi:cog" className="block" style={{ marginBottom: 4, marginLeft: 4 }} />
                        הגדרות
                    </p>
                </div>
                <div className="card-body">
                    {requestStudentApproval && renderField("בקשת אישור תלמיד", "כן", "mdi:account-check")}
                    {showNoteToStudent && renderField("הערה נראית לתלמיד", "כן", "mdi:eye")}
                    {isRepeating && renderField("אירוע חוזר", "כן", "mdi:repeat")}
                </div>
            </div>
        );
    };

    const renderNotes = () => {
        const notes = event.extendedProps?.notes;
        if (!notes) return null;

        return (
            <div className="card mb-4">
                <div className="card-header">
                    <p className="mb-0 flex items-center text-xl">
                        <Icon icon="mdi:note-text" className="block" style={{ marginBottom: 4, marginLeft: 4 }} />
                        הערות
                    </p>
                </div>
                <div className="card-body">
                    <p className="mb-0">{notes}</p>
                </div>
            </div>
        );
    };

    const getEventTypeDisplay = () => {
        const type = event.extendedProps?.type;
        const typeTexts = {
            lesson: "שיעור",
            lessonRequest: "בקשה לקביעת שיעור",
            test: "טסט",
            internalTest: "טסט פנימי",
            break: "הפסקה",
            vacation: "חופשה",
            cancelationRequest: "בקשה לביטול שיעור",
            google: "אירוע מיומן Google",
            googleFullDay: "אירוע מיומן Google"
        };
        return typeTexts[type] || type || "אירוע";
    };

    return (
        <MasterLayout>
            <Breadcrumb title="פרטי אירוע" />
            <div className="container mt-4">
                <div className="row">
                    <div className="col-12">
                        <div>
                            <div className="mb-20">
                                <div className="d-flex justify-content-between align-items-center">
                                    <h4 className="mb-0">{eventTypesTexts[event.extendedProps?.type]}</h4>
                                    <span className="badge bg-primary">{getEventTypeDisplay()}</span>
                                </div>
                            </div>
                            <div className="d-flex flex-column gap-2">
                                {renderStudentInfo()}
                                {renderDateTimeInfo()}
                                {renderLocationInfo()}
                                {renderFinancialInfo()}
                                {renderCarInfo()}
                                {renderSettingsInfo()}
                                {renderNotes()}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </MasterLayout>
    );
};

export default EventDetailsPage; 